import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { admin } from '../../api'
import { useAuth } from '../../auth'
import { formatDate } from '../../format'

export default function AdminUsers() {
  const { user: me } = useAuth()
  const queryClient = useQueryClient()
  const users = useQuery({ queryKey: ['admin', 'users'], queryFn: admin.users })

  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<'admin' | 'user'>('user')
  const [error, setError] = useState<string | null>(null)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })

  const create = useMutation({
    mutationFn: () =>
      admin.createUser({ email: email.trim(), displayName: displayName.trim(), password, role }),
    onSuccess: () => {
      setEmail('')
      setDisplayName('')
      setPassword('')
      setRole('user')
      setError(null)
      void invalidate()
    },
    onError: (err: Error) => setError(err.message),
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      admin.updateUser(id, body),
    onSuccess: invalidate,
    onError: (err: Error) => setError(err.message),
  })

  const remove = useMutation({
    mutationFn: (id: string) => admin.deleteUser(id),
    onSuccess: invalidate,
    onError: (err: Error) => setError(err.message),
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    create.mutate()
  }

  return (
    <section>
      <form className="row-form" onSubmit={submit}>
        <input
          type="text"
          placeholder="아이디 (이메일도 가능)"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
        <input
          placeholder="이름"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          required
        />
        <input
          type="password"
          placeholder="초기 비밀번호 (8자 이상)"
          minLength={8}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
        />
        <select value={role} onChange={(event) => setRole(event.target.value as 'admin' | 'user')}>
          <option value="user">사용자</option>
          <option value="admin">관리자</option>
        </select>
        <button type="submit" className="primary">
          계정 추가
        </button>
      </form>

      {error && (
        <p className="error banner" onClick={() => setError(null)}>
          {error}
        </p>
      )}

      <table className="entries">
        <thead>
          <tr>
            <th>이름</th>
            <th>아이디</th>
            <th>역할</th>
            <th>상태</th>
            <th className="num">생성일</th>
            <th className="actions" />
          </tr>
        </thead>
        <tbody>
          {users.data?.map((user) => (
            <tr key={user.id} className={user.disabled ? 'muted' : ''}>
              <td>{user.displayName}</td>
              <td className="mono small">{user.email}</td>
              <td>{user.role === 'admin' ? '관리자' : '사용자'}</td>
              <td>{user.disabled ? '비활성' : '활성'}</td>
              <td className="num small">{formatDate(user.createdAt)}</td>
              <td className="actions">
                <button
                  type="button"
                  className="link"
                  onClick={() => {
                    const next = window.prompt(`${user.displayName}의 새 비밀번호 (8자 이상)`)
                    if (!next) return
                    update.mutate({ id: user.id, body: { password: next } })
                  }}
                >
                  비밀번호 재설정
                </button>
                {user.id !== me?.id && (
                  <>
                    <button
                      type="button"
                      className="link"
                      onClick={() =>
                        update.mutate({ id: user.id, body: { disabled: !user.disabled } })
                      }
                    >
                      {user.disabled ? '활성화' : '비활성화'}
                    </button>
                    <button
                      type="button"
                      className="link danger"
                      onClick={() => {
                        if (!window.confirm(`${user.email} 계정을 삭제할까요? 공유 설정도 함께 사라집니다.`)) return
                        remove.mutate(user.id)
                      }}
                    >
                      삭제
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}
